/**
 * Services select panel
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import HttpStatus from 'http-status-codes';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
/**
 * Redux
 */
import { useDispatch, useSelector } from 'react-redux';
import {
  failUpdate,
  initUpdate, setStepValidation,
  successUpdate,
  wizardState,
  validateForm as validateFormAction,
} from '../../redux/wizardSlice';
import { updateLeadServices, leadState } from '../../redux/leadSlice';
import { lookupsState } from '../../redux/lookupsSlice';

/**
 * Components
 */
import Api from '../../helpers/api';
import ServiceOption from '../../components/ServiceOption';
import { getToken } from '../../helpers/auth';

const useStyles = makeStyles(() => ({
  serviceName: {
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 10,
    display: 'flex',
    alignContent: 'flex-end',
  },
  mandatory: {
    display: 'none',
  },
}));

/**
 * @returns {JSX.Element}
 */
function ServicesSelect() {
  // window?.ecommerceData?.services;
  const allServices = useSelector(lookupsState).configuration.services;
  const [services, setServices] = useState([]);
  const dispatch = useDispatch();
  const { actionRequired, validStep, validateForm } = useSelector(wizardState);
  const { selectedServices, leadId } = useSelector(leadState);
  const classes = useStyles();
  const { t } = useTranslation();
  const requiredTypes = ['checkboxes-required', 'radio'];
  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    isInitialValid: false,
    initialValues: {
    },
    validate: () => {
      const errors = {};
      const selected = _.uniq(selectedServices.map((el) => el.service));
      const required = _
        .uniq(services.filter((el) => requiredTypes.includes(el.type)).map((el) => el.service));
      const servicesWithErrors = _.without(required, ...selected);

      for (let i = 0; i < servicesWithErrors.length; i += 1) {
        const el = servicesWithErrors[i];
        errors[el] = `Field required: ${services.filter((e) => e.service === el)[0].serviceName}`;
      }
      return errors;
    },
  });

  useEffect(() => {
    if (formik.dirty && formik.isValid !== validStep) {
      dispatch(setStepValidation(formik.isValid));
    }
  }, [formik.isValid]);
  useEffect(() => {
    // If there aren't any selected services, we can'tn use setTouched
    if (validateForm) {
      if (selectedServices?.length) {
        formik.setTouched();
      } else {
        const selected = _.uniq(selectedServices.map((el) => el.service));
        const required = _
          .uniq(services.filter((el) => requiredTypes.includes(el.type)).map((el) => el.service));
        const servicesWithErrors = _.without(required, ...selected);
        if (!servicesWithErrors.length) {
          dispatch(successUpdate());
        }
      }
    }
  }, [validateForm]);
  // Reset formik is valid on mount
  useEffect(() => {
    dispatch(validateFormAction(false));
  }, []);
  /**
   * Merge services and load into formik structure
   */
  const loadServices = () => {
    const mergedServices = [...allServices.all_vehicles, ...allServices.this_vehicle];
    const servicePlain = [];
    // get service list from wordpress configuration
    mergedServices.forEach((serv, index) => {
      serv.options?.forEach((opt, oIndex) => {
        servicePlain.push({
          id: `${index}-${oIndex}`,
          service: `${index}`,
          serviceName: serv.name,
          type: serv.type,
          name: opt.name,
          value: opt.value,
        });
      });
    });
    setServices(servicePlain);
    if (selectedServices?.length) {
      // Re-hydrate formik instance
      selectedServices.forEach((el) => {
        switch (el.type) {
          case 'checkboxes-required':
            formik.setFieldValue(el.id, true);
            break;
          case 'checkboxes':
            formik.setFieldValue(el.id, true);
            break;
          case 'radio':
            formik.setFieldValue(el.service, el.id);
            break;
          default:
        }
      });
    }
  };
  /**
   * Initial load
   * Merge all services in one array
   */
  useEffect(() => {
    loadServices();
    dispatch(setStepValidation(formik.isValid));
  }, []);
  /**
   * Submit form routine
   * @returns {Promise<void>}
   */
  const submitForm = async () => {
    const servicesFormatted = selectedServices.map((el) => ({
      service: el.serviceName,
      name: el.name,
      price: el.value,
    }));

    const res = await Api.patch({
      entryPoint: `/lead/${leadId}`,
      headers: { 'ga-token': getToken(leadId) },
      data: {
        id: leadId,
        services: servicesFormatted,
      },
    });

    if (res.status !== HttpStatus.OK) {
      dispatch(failUpdate());
      return;
    }
    dispatch(successUpdate());
  };
  /**
   * Dispatch submit form
   */
  useEffect(() => {
    if (actionRequired) {
      dispatch(initUpdate());
      submitForm();
    }
  }, [actionRequired]);
  useEffect(() => {
    formik.validateForm();
  }, [selectedServices]);
  /**
   * Update selected and mandatory services into redux state
   */
  useEffect(() => {
    dispatch(updateLeadServices(services
      .filter(
        (el) => Boolean(formik.values[el.id] === true)
                || formik.values[el.service] === el.id
                || el.type === 'mandatory',
      )));
  }, [formik.values, services]);
  const getServiceTitle = (el) => {
    if (requiredTypes.includes(el.type)) {
      return `${el.serviceName} (${t('choose at least one service')})`;
    }
    return el.serviceName;
  };
  const renderOptions = () => (
    <div className="col-xs-12">
      {services.map((el, i) => (
        <div key={el.id} className={el.type === 'mandatory' ? classes.mandatory : ''}>
          {i === 0 || services[i].serviceName !== services[i - 1].serviceName ? (
            <div className={classes.serviceName}>
              {getServiceTitle(el)}
            </div>
          )
            : ''}
          <ServiceOption
            formik={formik}
            option={el}
          />
        </div>
      ))}
    </div>
  );
  return (
    <div className="col-xs-12 ecommerce-services">
      {renderOptions()}
    </div>
  );
}

export default ServicesSelect;
